import { DEFAULT_FPS } from '../../../utils/timecode';
import {
  canonicalMediaKey,
  MaCsvRow,
  MaOptionChoiceSource,
  MaOptionSlotSource,
  MaTimeValue,
  MaWarning,
  maTimeToSeconds,
  stripExtension,
} from './maTypes';

/**
 * オプションスロット（`(x)` / `[x]` マーカー）の共有ロジック。
 *
 * RPP のポイントマーカー由来（parseRppProject）と、保存 CSV の行由来
 * （parseMaCsv）の両方から同じ規則で MaOptionSlotSource を構築する。
 * 保存 CSV にもスロット / パレット構造を書き出す（serializeMaCsv）ことで、
 * タイムライン編集の保存後もオプション切替を維持できる（ラウンドトリップ）。
 *
 * 規則:
 * - `(x)` = オプション差し込みスロット。`(x=候補キー)` で初期選択を指定できる
 * - 最初の `[` マーカー以降は「オプション候補置き場（パレット領域）」として
 *   本編から除外される。各セクションは次の `[` マーカーまで
 * - セクション内で同じ開始位置に並ぶ候補行群（SE + VC 等）が 1 候補。
 *   候補行はクラスタ先頭を 0 とする相対時刻で保持される
 * - 候補リージョン / 動画のカバー範囲内に置かれた点マーカー（end なし）は
 *   同じ候補の追加発音として合流する（候補内の時刻指定配置）。
 *   end を持つ行（リージョン / 動画）は常に新しい候補を開始する
 */

/** `(x)` = オプション差し込みスロット（内側は `名前=初期選択` を許容） */
export const SLOT_NAME_RE = /^\((.+)\)$/;
/** `[x]` = オプション候補セクション見出し */
export const SECTION_NAME_RE = /^\[(.+)\]$/;

/** 同一開始位置（= 同一候補バンドル）とみなす開始差の許容（秒） */
const OPTION_CLUSTER_EPSILON = 0.01;
/** 同名スロットマーカーを重複とみなす位置差の許容（秒） */
const SLOT_DUPLICATE_EPSILON = 0.05;
/**
 * 「動画のみ（無音）候補」の診断警告で、音声付き候補の近傍とみなす開始差（秒）。
 * 差し替え動画の開始が Region と揃わず別候補に分かれる配置ミス
 * （実例: 0.17 秒 / 1 秒ずれ）を検出する。意図的な「無音の既定候補」は
 * 通常これより離れた位置に置かれるため対象にならない
 */
const VIDEO_ONLY_CLUSTER_NEAR_EPSILON = 1;

/**
 * 順序・近接比較用の秒換算。frames / timecode は既定 fps で換算する
 * （実 fps はリンク時まで未確定のため、比較・クラスタ判定のみに使う）
 */
function comparableSeconds(value: MaTimeValue): number {
  return maTimeToSeconds(value, DEFAULT_FPS);
}

/**
 * 時刻の減算。同種 kind（frames 同士 / seconds 同士）は kind を保って
 * 減算し、実 fps 換算をリンク時まで遅延できるようにする
 */
function subtractTime(a: MaTimeValue, b: MaTimeValue): MaTimeValue {
  if (a.kind === 'frames' && b.kind === 'frames') {
    return { kind: 'frames', frames: a.frames - b.frames };
  }
  if (a.kind === 'seconds' && b.kind === 'seconds') {
    return { kind: 'seconds', seconds: a.seconds - b.seconds };
  }
  return { kind: 'seconds', seconds: comparableSeconds(a) - comparableSeconds(b) };
}

/** `(名前=初期選択)` の内側を名前と初期選択キーに分割する */
function parseSlotInner(inner: string): { name: string; selection: string | null } {
  const eq = inner.indexOf('=');
  if (eq <= 0 || eq === inner.length - 1) return { name: inner, selection: null };
  return { name: inner.slice(0, eq), selection: inner.slice(eq + 1) };
}

/** `(x)` / `[x]` マーカー 1 つぶんの入力（inner = 括弧の内側） */
export interface OptionMarkerInput {
  line: number;
  inner: string;
  start: MaTimeValue;
}

/** パレット領域内の候補行 1 つぶんの入力 */
export interface OptionCandidateInput {
  line: number;
  media: string;
  start: MaTimeValue;
  end: MaTimeValue | null;
  /** ソースオフセット（動画候補の SOFFS。音声行は null） */
  offset: MaTimeValue | null;
  track: number | string | null;
  gainDb: number | null;
  /** クリップ表示色（#rrggbb。MaCsvRow.color と同じ） */
  color?: string | null;
  /**
   * 同一開始クラスタ内の並び順（小さいほど先頭 = 候補ラベル。未指定は 0 で行順のまま）。
   * RPP はリージョン < 通常マーカー < 動画を指定する
   */
  clusterOrder?: number;
  /**
   * 動画の候補行（パレット領域の動画アイテム由来）。
   * 動画のみ（無音）候補の診断警告に使う（未指定 = 音声行）
   */
  isVideo?: boolean;
}

/**
 * `(x)` スロットと `[x]` セクションを対応づけ、セクション内の候補行を
 * 開始位置クラスタ（= 同時に鳴らすバンドル）ごとの候補にまとめる。
 */
export function buildOptionSlotSources(params: {
  slotMarkers: OptionMarkerInput[];
  sectionMarkers: OptionMarkerInput[];
  /** パレット領域内の候補行（呼び出し側で paletteStart 以降に絞り込み済み） */
  candidates: OptionCandidateInput[];
  warnings: MaWarning[];
}): MaOptionSlotSource[] {
  const { slotMarkers, sectionMarkers, candidates, warnings } = params;

  // パレット領域（オプション候補置き場）= 最初の [ マーカー以降すべて
  const paletteStart =
    sectionMarkers.length > 0
      ? Math.min(...sectionMarkers.map(s => comparableSeconds(s.start)))
      : Infinity;

  // ---- [x] セクション: 位置順に区切る（各セクションは次の [ マーカーまで） ----
  interface OptionSection {
    line: number;
    name: string;
    choices: MaOptionChoiceSource[];
    used: boolean;
  }
  const sortedSections = [...sectionMarkers].sort(
    (a, b) => comparableSeconds(a.start) - comparableSeconds(b.start) || a.line - b.line,
  );
  const sectionNameSeen = new Set<string>();
  const sections: OptionSection[] = sortedSections.map((marker, i) => {
    const name = marker.inner;
    const nameKey = canonicalMediaKey(name);
    if (sectionNameSeen.has(nameKey)) {
      warnings.push({
        line: marker.line,
        message: `オプションセクション「[${name}]」が重複しています（先のセクションを使用）`,
      });
    }
    sectionNameSeen.add(nameKey);

    const sectionStart = comparableSeconds(marker.start);
    const next = sortedSections[i + 1];
    const sectionEnd = next ? comparableSeconds(next.start) : Infinity;
    const sectionRows = candidates
      .filter(c => comparableSeconds(c.start) >= sectionStart && comparableSeconds(c.start) < sectionEnd)
      .sort((a, b) => comparableSeconds(a.start) - comparableSeconds(b.start) || a.line - b.line);

    // 開始位置クラスタ = 1 候補（クラスタ先頭を 0 とする相対時刻へ変換）
    const choices: MaOptionChoiceSource[] = [];
    /** 診断用のクラスタ情報（動画のみ候補の近傍警告） */
    const clusterMeta: Array<{
      line: number;
      startSeconds: number;
      media: string;
      label: string;
      videoOnly: boolean;
    }> = [];
    const labelCount = new Map<string, number>();
    let cluster: OptionCandidateInput[] = [];
    /** クラスタ内の end 付き行（リージョン / 動画）がカバーする終端（秒） */
    let clusterCoverageEnd: number | null = null;
    const flushCluster = () => {
      if (cluster.length === 0) return;
      const clusterStart = cluster[0].start;
      const ordered = [...cluster].sort((a, b) => (a.clusterOrder ?? 0) - (b.clusterOrder ?? 0));
      const label = stripExtension(ordered[0].media);
      const count = (labelCount.get(label) ?? 0) + 1;
      labelCount.set(label, count);
      choices.push({
        key: count === 1 ? label : `${label}~${count}`,
        label,
        rows: ordered.map(c => ({
          line: c.line,
          media: c.media,
          start: subtractTime(c.start, clusterStart),
          end: c.end !== null ? subtractTime(c.end, clusterStart) : null,
          offset: c.offset,
          track: c.track,
          gainDb: c.gainDb,
          color: c.color ?? null,
        })),
      });
      clusterMeta.push({
        line: ordered[0].line,
        startSeconds: comparableSeconds(clusterStart),
        media: ordered[0].media,
        label,
        videoOnly: cluster.every(c => c.isVideo),
      });
      cluster = [];
      clusterCoverageEnd = null;
    };
    sectionRows.forEach(row => {
      if (
        cluster.length > 0 &&
        comparableSeconds(row.start) - comparableSeconds(cluster[0].start) > OPTION_CLUSTER_EPSILON
      ) {
        // 点マーカー（end なし）が現クラスタのリージョン / 動画のカバー範囲内に
        // あるときは、同じ候補の追加発音として合流する（候補内の時刻指定配置。
        // 例: カットイン Region 内の告知 SE マーカー）。カバー終端ちょうどは新候補
        const joinsCoverage =
          row.end === null &&
          clusterCoverageEnd !== null &&
          comparableSeconds(row.start) < clusterCoverageEnd - OPTION_CLUSTER_EPSILON;
        if (!joinsCoverage) flushCluster();
      }
      cluster.push(row);
      if (row.end !== null) {
        clusterCoverageEnd = Math.max(clusterCoverageEnd ?? -Infinity, comparableSeconds(row.end));
      }
    });
    flushCluster();

    // 動画のみ（無音）候補の診断: 近傍（1 秒以内）に音声付き候補があるのに
    // 開始がずれて別候補になっている動画は、開始を揃え損ねた配置ミスの
    // 可能性が高いので警告する（候補化・再生の挙動は変えない）
    clusterMeta.forEach(meta => {
      if (!meta.videoOnly) return;
      const near = clusterMeta.find(
        other =>
          !other.videoOnly &&
          Math.abs(other.startSeconds - meta.startSeconds) <= VIDEO_ONLY_CLUSTER_NEAR_EPSILON,
      );
      if (!near) return;
      const delta = Math.round(Math.abs(near.startSeconds - meta.startSeconds) * 1000) / 1000;
      warnings.push({
        line: meta.line,
        message:
          `セクション「[${name}]」の動画「${meta.media}」は候補「${near.label}」と開始が ` +
          `${delta} 秒ずれており、動画のみの無音候補になっています（同じ候補にする場合は開始を揃えてください）`,
      });
    });

    return { line: marker.line, name, choices, used: false };
  });

  /** 名前一致する最初のセクション（重複時は位置が先のものが勝つ） */
  const findSection = (name: string): OptionSection | undefined => {
    const nameKey = canonicalMediaKey(name);
    return sections.find(s => canonicalMediaKey(s.name) === nameKey);
  };

  // ---- (x) スロット: 重複を畳んでセクションと対応づける ----
  interface AcceptedSlot {
    line: number;
    name: string;
    selection: string | null;
    start: MaTimeValue;
  }
  const acceptedSlots: AcceptedSlot[] = [];
  [...slotMarkers]
    .sort((a, b) => a.line - b.line)
    .forEach(marker => {
      const { name, selection } = parseSlotInner(marker.inner);
      if (comparableSeconds(marker.start) >= paletteStart) {
        warnings.push({
          line: marker.line,
          message: `オプションマーカー「(${name})」はオプション候補領域内のため無視しました`,
        });
        return;
      }
      const duplicate = acceptedSlots.find(
        s =>
          canonicalMediaKey(s.name) === canonicalMediaKey(name) &&
          Math.abs(comparableSeconds(s.start) - comparableSeconds(marker.start)) <=
            SLOT_DUPLICATE_EPSILON,
      );
      if (duplicate) {
        warnings.push({
          line: marker.line,
          message: `オプションマーカー「(${name})」が同じ位置に重複しています（先の行を使用）`,
        });
        return;
      }
      acceptedSlots.push({ line: marker.line, name, selection, start: marker.start });
    });

  const optionSlots: MaOptionSlotSource[] = [];
  acceptedSlots
    .sort((a, b) => comparableSeconds(a.start) - comparableSeconds(b.start) || a.line - b.line)
    .forEach(slot => {
      const section = findSection(slot.name);
      if (!section) {
        warnings.push({
          line: slot.line,
          message: `オプションマーカー「(${slot.name})」に対応する「[${slot.name}]」セクションが見つかりません`,
        });
        return;
      }
      section.used = true;
      if (section.choices.length === 0) {
        warnings.push({
          line: section.line,
          message: `オプションセクション「[${slot.name}]」に候補リージョン / マーカーがありません`,
        });
        return;
      }
      // 初期選択（`(x=候補キー)`）。無指定・不一致は先頭候補
      let defaultChoiceKey = section.choices[0].key;
      if (slot.selection !== null) {
        const chosen = section.choices.find(c => c.key === slot.selection);
        if (chosen) {
          defaultChoiceKey = chosen.key;
        } else {
          warnings.push({
            line: slot.line,
            message: `オプション「(${slot.name})」の初期選択「${slot.selection}」が候補にありません（先頭候補を使用）`,
          });
        }
      }
      optionSlots.push({
        key: `${slot.name}@${comparableSeconds(slot.start)}`,
        name: slot.name,
        start: slot.start,
        line: slot.line,
        choices: section.choices,
        defaultChoiceKey,
      });
    });

  sections.forEach(section => {
    if (!section.used) {
      warnings.push({
        line: section.line,
        message: `オプションセクション「[${section.name}]」を参照する「(${section.name})」マーカーがありません`,
      });
    }
  });

  return optionSlots;
}

/**
 * CSV 行からオプションマーカー行（`(x)` / `[x]`）を抽出し、
 * 本編行とオプションスロットに分離する（保存 CSV のラウンドトリップ用）。
 * マーカー行がなければ入力の行をそのまま返す。
 */
export function extractOptionSlotsFromRows(
  rows: MaCsvRow[],
  warnings: MaWarning[],
): { rows: MaCsvRow[]; optionSlots: MaOptionSlotSource[] } {
  const slotMarkers: OptionMarkerInput[] = [];
  const sectionMarkers: OptionMarkerInput[] = [];
  const rest: MaCsvRow[] = [];
  rows.forEach(row => {
    const slotMatch = row.media.match(SLOT_NAME_RE);
    if (slotMatch) {
      slotMarkers.push({ line: row.line, inner: slotMatch[1], start: row.start });
      return;
    }
    const sectionMatch = row.media.match(SECTION_NAME_RE);
    if (sectionMatch) {
      sectionMarkers.push({ line: row.line, inner: sectionMatch[1], start: row.start });
      return;
    }
    rest.push(row);
  });

  if (slotMarkers.length === 0 && sectionMarkers.length === 0) {
    return { rows, optionSlots: [] };
  }

  const paletteStart =
    sectionMarkers.length > 0
      ? Math.min(...sectionMarkers.map(s => comparableSeconds(s.start)))
      : Infinity;
  const mainRows = rest.filter(r => comparableSeconds(r.start) < paletteStart);
  const candidates: OptionCandidateInput[] = rest
    .filter(r => comparableSeconds(r.start) >= paletteStart)
    .map(r => ({
      line: r.line,
      media: r.media,
      start: r.start,
      end: r.end,
      offset: r.offset,
      track: r.track,
      gainDb: r.gainDb,
      color: r.color,
    }));

  const optionSlots = buildOptionSlotSources({ slotMarkers, sectionMarkers, candidates, warnings });
  return { rows: mainRows, optionSlots };
}
